import PageLoader from '@components/PageLoader'
import { useAdminOverview } from '@hooks/useAdminOverview'
import React from 'react'
import { AdminHeading } from '../AdminLayout.styles'
import { formatAmount, formatDate, formatName } from '../adminFormat'
import { BottomGrid, ErrorState, TopGrid } from './OverviewPage.styles'
import StatCard, { StatCardRow } from './StatCard'
import TrendChart from './TrendChart'

/**
 * Admin overview home: a card per resource (Users + Trips on top, then Events,
 * Ideas, Expenses), each showing a count and the most-recent rows that link to the
 * matching detail page, with a "See all" link to the full list. A daily trend
 * across all five series sits below. Everything is scoped to the admin layout's
 * global creation-date filter.
 */
const OverviewPage: React.FC = () => {
	const { data, isLoading, isError } = useAdminOverview()

	if (isLoading) {
		return (
			<>
				<AdminHeading>Overview</AdminHeading>
				<PageLoader label="Loading overview…" />
			</>
		)
	}

	if (isError || !data) {
		return (
			<>
				<AdminHeading>Overview</AdminHeading>
				<ErrorState>Could not load the overview. Please try again.</ErrorState>
			</>
		)
	}

	const userRows: StatCardRow[] = data.users.recent.map(u => ({
		id: u.id,
		to: `/admin/users/${u.id}`,
		primary: formatName(u.firstName, u.lastName),
		secondary: `${u.email} · ${u.tripCount} ${u.tripCount === 1 ? 'trip' : 'trips'}`,
		meta: formatDate(u.signupDate),
		deleted: u.isDeleted,
	}))

	const tripRows: StatCardRow[] = data.trips.recent.map(t => ({
		id: t.id,
		to: `/admin/trips/${t.id}`,
		primary: t.name || 'Untitled trip',
		secondary: `${t.ownerName} · ${t.memberCount} ${t.memberCount === 1 ? 'member' : 'members'}`,
		meta: formatDate(t.createdAt),
		deleted: t.isDeleted,
	}))

	const eventRows: StatCardRow[] = data.events.recent.map(e => ({
		id: e.id,
		to: `/admin/events/${e.id}`,
		primary: e.name,
		secondary: `${e.tripName} · Day ${e.tripDayIndex + 1}`,
		meta: formatDate(e.createdAt),
		deleted: e.isDeleted,
	}))

	const ideaRows: StatCardRow[] = data.ideas.recent.map(i => ({
		id: i.id,
		to: `/admin/ideas/${i.id}`,
		primary: i.name,
		secondary: i.scope === 'trip' ? (i.tripName ?? 'Trip idea') : 'Personal idea',
		meta: formatDate(i.createdAt),
		deleted: i.isDeleted,
	}))

	const expenseRows: StatCardRow[] = data.expenses.recent.map(x => ({
		id: x.id,
		to: `/admin/expenses/${x.id}`,
		primary: x.name,
		secondary: `${formatAmount(x.amount, x.currencyCode)} · ${x.tripName}`,
		meta: formatDate(x.createdAt),
		deleted: x.isDeleted,
	}))

	return (
		<>
			<AdminHeading>Overview</AdminHeading>

			<TopGrid>
				<StatCard
					label="New users"
					count={data.users.count}
					seeAllTo="/admin/users"
					rows={userRows}
					emptyText="No users in this range."
				/>
				<StatCard
					label="New trips"
					count={data.trips.count}
					seeAllTo="/admin/trips"
					rows={tripRows}
					emptyText="No trips in this range."
				/>
			</TopGrid>

			<BottomGrid>
				<StatCard
					label="New events"
					count={data.events.count}
					seeAllTo="/admin/events"
					rows={eventRows}
					emptyText="No events in this range."
				/>
				<StatCard
					label="New ideas"
					count={data.ideas.count}
					seeAllTo="/admin/ideas"
					rows={ideaRows}
					emptyText="No ideas in this range."
				/>
				<StatCard
					label="New expenses"
					count={data.expenses.count}
					seeAllTo="/admin/expenses"
					rows={expenseRows}
					emptyText="No expenses in this range."
				/>
			</BottomGrid>

			<TrendChart trend={data.trend} />
		</>
	)
}

export default OverviewPage
